import { selectActiveEmailAccounts } from '@mm/state/selectors/emailAccountsSelectors';
import { Alert, Checkbox, Heading } from '@mm/components';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import GeneralConfirmModal from '../../../../../common/modals/GeneralConfirmModal/GeneralConfirmModal';
import styles from './ContactDeleteModal.module.css';

interface Props {
  showEraseRemoteMessagesOption: boolean;
  show: boolean;
  emailAddress: string;
  onClose: () => void;
  onDelete: (eraseRemoteMessages: boolean) => void;
}

export const ContactDeleteModal = ({
  showEraseRemoteMessagesOption,
  show,
  emailAddress,
  onClose,
  onDelete,
}: Props) => {
  const emailAccounts = useSelector(selectActiveEmailAccounts).filter(
    account => account.canEraseRemoteMessages,
  );
  const allowEraseRemoteMessages = emailAccounts.length > 0;
  const [eraseRemoteMessages, setEraseRemoteMessages] = useState(false);
  const [reallyEraseRemoteMessages, setReallyEraseRemoteMessages] =
    useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);

  useEffect(() => {
    if (!show) {
      setEraseRemoteMessages(false);
      setReallyEraseRemoteMessages(false);
    }
  }, [show]);

  if (!show) {
    return null;
  }

  return (
    <div className={styles.overlay}>
      <GeneralConfirmModal
        modalClassname={styles.content}
        headerText="Are you sure you want to delete this contact?"
        css={{
          header: styles.header,
          body: styles.modalBody,
          footer: styles.modalFooter,
        }}
        messageText={
          <div>
            <div className={styles.message}>
              There is no way to undo this action. All records of this contact,
              including past conversations and attributes, that are stored on
              Customers.ai's servers, will be destroyed.
            </div>
            {allowEraseRemoteMessages && showEraseRemoteMessagesOption && (
              <div className={styles.gdprOptionArea}>
                <Heading size={4} className={styles.contentHeading}>
                  GDPR Compliance
                </Heading>
                <Checkbox
                  isChecked={eraseRemoteMessages}
                  onToggle={e => setEraseRemoteMessages(e.target.checked)}
                  label="Permanently delete all email messages involving this contact from all connected email accounts"
                  className={styles.gdprOption}
                  labelHelpTextClassName={styles.gdprOptionLabel}
                />
                {eraseRemoteMessages && (
                  <Alert
                    type="warning"
                    showCloseIcon={false}
                    css={{ container: styles.gdprAlert }}
                  >
                    This will{' '}
                    <strong>permanently delete all email messages</strong> to or
                    from the following email address:{' '}
                    <strong>{emailAddress}</strong>.
                    <br />
                    <br />
                    Email messages will be <strong>
                      permanently deleted
                    </strong>{' '}
                    from the following mailboxes:
                    <ul>
                      {emailAccounts.map(account => (
                        <li key={account.id}>{account.emailAddress}</li>
                      ))}
                    </ul>
                  </Alert>
                )}
                {eraseRemoteMessages && (
                  <Checkbox
                    isChecked={reallyEraseRemoteMessages}
                    onToggle={e =>
                      setReallyEraseRemoteMessages(e.target.checked)
                    }
                    label="I accept that this option will permanently remove messages from my email accounts, and there is no way to undo this action"
                    labelHelpTextClassName={styles.gdprConfirmOptionLabel}
                  />
                )}
              </div>
            )}
          </div>
        }
        cancelButtonFirst
        okButtonText="Delete"
        okButtonStyling={{ red: true }}
        primaryButtonDisabled={
          deleteInProgress ||
          (eraseRemoteMessages && !reallyEraseRemoteMessages)
        }
        closeModal={onClose}
        onCloseHandler={async ok => {
          if (ok) {
            setDeleteInProgress(true);
            try {
              await onDelete(reallyEraseRemoteMessages);
              onClose();
            } finally {
              setDeleteInProgress(false);
            }
          } else {
            onClose();
          }
        }}
      />
    </div>
  );
};
